/**
 * Compute which filters are effectively applied to a chart, replicating the back logic
 * (analytics-back: Charts.controller.v2.js `convertFilters` / `getSpecialFilters`,
 * and websockets/charts.ws.js `getChartsForPage`):
 *
 * - A page filter applies to a chart when its dictionaryPath collection is the chart collection,
 *   when it has no dictionaryPath, or when one of its links targets the chart collection.
 *   "specialDate" is always kept, then only mapped on the chart if the chart has a filterDateAttribute.
 * - When the page and the widget filter the same field, the PAGE wins (`{ ...chart.filters, ...pageFilters }`).
 * - The page special date is always mapped on `request.filterDateAttribute`: it overrides any other
 *   filter on this attribute, from the widget or from the page (`getSpecialFilters`).
 */
import lod_ from "lodash";
import { getLabel } from "helpers/dictionary";

export const SPECIAL_DATE_KEY = "specialDate";

const RANGE_METHODS = ["$gt", "$gte", "$lt", "$lte"];
const SIMPLE_METHODS = ["$in", "$nin", "$eq", "$ne", ...RANGE_METHODS];

/**
 * Get the collection(s) of a chart, same rules as the back
 * @param {object} chart
 * @return {{collection: string|undefined, multiCollections: [string]}}
 */
export function getChartCollections(chart) {
	const request = chart?.request ?? {};
	let collection = request.collection;

	const referenceSource = (request.sources ?? []).find(source => source?.reference);
	if (referenceSource) {
		collection = referenceSource.collection;
	}
	if (request.reference && lod_.isString(request.reference)) {
		collection = request.reference;
	}

	let multiCollections = [];
	if (!lod_.isNil(chart?.collections) && !lod_.isEmpty(chart.collections)) {
		multiCollections = chart.collections.map(c => c.collection);
	}

	return { collection, multiCollections };
}

/**
 * Date instances (or already serialized dates) as ISO strings
 */
function toIso(value) {
	return value instanceof Date ? value.toISOString() : value;
}

function isEmptyValue(value) {
	if (lod_.isNil(value) || value === "") return true;
	if (Array.isArray(value)) return value.length === 0;
	if (lod_.isPlainObject(value)) return Object.keys(value).length === 0;
	return false;
}

function stringify(value) {
	if (lod_.isString(value)) return value;
	if (value instanceof Date) return value.toISOString();
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
}

/**
 * Label of a value coming from a list of codes ({ value: label | { fr: label } })
 */
function codeLabel(codes, value, language) {
	const label = !lod_.isNil(codes) && !lod_.isObject(value) ? codes[value] : undefined;
	if (lod_.isNil(label)) return stringify(value);
	return lod_.isPlainObject(label) ? getLabel(label, language) : String(label);
}

/**
 * Describe a mongo-like filter value as { method, values, range }
 * - method: "$in" | "$nin" | "$eq" | "$ne" | "$gt" | "$gte" | "$lt" | "$lte" | "$regex" | "between" | "raw"
 * - values: displayable strings (dates are ISO strings)
 * - range: only for "between", bounds by operator ($gte, $lte...)
 * Unknown shapes are returned as "raw" with the JSON text
 * @param {any} value - filter value ({ $in: [...] }, "text", [..], { $gte, $lte }...)
 * @param {object} [codes] - labels of the values
 * @param {string} [language]
 */
export function describeFilterValue(value, codes = null, language = "fr") {
	const text = v => codeLabel(codes, toIso(v), language);

	if (Array.isArray(value)) {
		return { method: "$in", values: value.map(text) };
	}
	if (lod_.isPlainObject(value)) {
		const keys = Object.keys(value);
		if (keys.includes("$regex") && keys.every(k => ["$regex", "$options"].includes(k))) {
			return { method: "$regex", values: [stringify(value.$regex)] };
		}
		const rangeKeys = keys.filter(k => RANGE_METHODS.includes(k));
		if (rangeKeys.length > 1 && rangeKeys.length === keys.length) {
			return { method: "between", values: [], range: lod_.mapValues(value, toIso) };
		}
		if (keys.length === 1 && SIMPLE_METHODS.includes(keys[0])) {
			const inner = value[keys[0]];
			return {
				method: keys[0],
				values: Array.isArray(inner) ? inner.map(text) : [text(inner)]
			};
		}
		return { method: "raw", values: [stringify(value)] };
	}
	return { method: "$eq", values: [text(value)] };
}

/**
 * Label of a dictionary field (path "a.b" => items.a.items.b)
 */
function getFieldItem(dictionary, path) {
	if (lod_.isNil(dictionary?.items) || lod_.isNil(path)) return undefined;
	return lod_.get(dictionary.items, path.replaceAll(".", ".items."));
}

/**
 * Text of a label (string or { fr: label }), fallback when it is missing or empty
 */
function resolveLabel(label, fallback, language) {
	if (lod_.isPlainObject(label)) return getLabel(label, language) || fallback;
	return lod_.isString(label) ? label : fallback;
}

function getFieldLabel(dictionary, path, language) {
	return resolveLabel(getFieldItem(dictionary, path)?.label, path, language);
}

/**
 * Label of a page filter from its definition, fallback on the attribute
 */
function getPageFilterLabel(definition, key, language) {
	return resolveLabel(definition?.label, key, language);
}

/**
 * Codes labels of a page filter definition ({ values: [{ value, label }] })
 */
function getDefinitionCodes(definition) {
	if (!Array.isArray(definition?.values)) return null;
	return definition.values.reduce((acc, item) => {
		if (!lod_.isNil(item?.value)) acc[item.value] = item.label;
		return acc;
	}, {});
}

/**
 * Value of a page filter as mapped by the back:
 * datetime => only the non empty bounds, others => the value
 * @return {any|undefined} undefined when the filter is not sent
 */
function getMappedPageValue(filter) {
	if (filter.type === "datetime") {
		const range = {};
		for (const bound of RANGE_METHODS) {
			const boundValue = toIso(filter.value?.[bound]);
			if (!lod_.isNil(boundValue) && boundValue !== "") {
				range[bound] = boundValue;
			}
		}
		return lod_.isEmpty(range) ? undefined : range;
	}
	return filter.value;
}

/**
 * Page filters that reach the chart, split in:
 * - main: sent with the chart filters, indexed by the name used in the query
 * - perCollection: multi collections charts, sent to one sub collection only
 * Same rules as `convertFilters`
 */
function getApplicablePageFilters(pageFilters, collection, multiCollections) {
	const isMulti = !lod_.isEmpty(multiCollections);
	const mainEntries = [];
	const perCollection = [];

	for (const key of Object.keys(pageFilters ?? {})) {
		const filter = pageFilters[key];
		if (lod_.isNil(filter)) continue;

		for (const link of filter.links ?? []) {
			const linkCollection = link.dictionary ?? link.dictionaryPath?.split(".")[0];
			const entry = {
				sourceKey: key,
				mapKey: link.path,
				viaLink: true,
				filter: { ...link, name: link.path, value: filter.value }
			};
			if (linkCollection === collection) {
				mainEntries.push(entry);
			}
			if (multiCollections.includes(linkCollection)) {
				perCollection.push({ ...entry, collection: linkCollection });
			}
		}

		const keyCollection = filter.dictionaryPath?.split(".")[0];
		const entry = { sourceKey: key, mapKey: key, viaLink: false, filter };
		if ((keyCollection === collection || !filter.dictionaryPath) && !isMulti) {
			mainEntries.push(entry);
		}
		if (key === SPECIAL_DATE_KEY) {
			mainEntries.push(entry);
		}
		if (multiCollections.includes(keyCollection)) {
			perCollection.push({ ...entry, collection: keyCollection });
		}
	}

	// Same as the back: one entry by key, the last one wins (`filters[key] = ...`)
	const main = {};
	for (const entry of mainEntries) {
		main[entry.mapKey] = entry;
	}
	return { main, perCollection };
}

/**
 * Compute the filters context of a chart
 * @param {object} chart - chart (request, filters, dictionary, collections)
 * @param {object} pageFilters - page filters sent to the back, by attribute ({ name, value, type, dictionaryPath, links })
 * @param {[object]} [pageFilterDefs] - page filter definitions (label, values...)
 * @param {object} [options]
 * @param {string} [options.language]
 * @param {object} [options.pageDisplayDates] - dates displayed by the page date filters, by attribute ({ $gte, $lte })
 * @param {string} [options.defaultDateLabel]
 * @return {{
 *   sources: [{ collection: string, label: string }],
 *   widgetRows: [{ key, label, method, values, range, overridden }],
 *   pageRows: [{ key, label, method, values, range, isSpecialDate, viaLink, overridden }],
 *   count: number,
 *   hasOverride: boolean
 * }}
 */
export function computeChartFiltersContext(chart, pageFilters, pageFilterDefs = [], options = {}) {
	const language = options.language ?? "fr";
	// Label of the special date when its target field has no label in the dictionary
	const defaultDateLabel = options.defaultDateLabel ?? "Période";
	const dictionary = chart?.dictionary;
	const request = chart?.request ?? {};
	const definitions = pageFilterDefs ?? [];
	const { collection, multiCollections } = getChartCollections(chart);

	// 1- Source(s)
	const sourceCodes = multiCollections.length > 0 ? multiCollections : [collection];
	const sources = lod_.uniq(sourceCodes.filter(code => !lod_.isEmpty(code))).map(code => {
		const dictionaryLabel = code === request.collection ? dictionary?.label : undefined;
		return {
			collection: code,
			label: lod_.isPlainObject(dictionaryLabel)
				? getLabel(dictionaryLabel, language) || code
				: code
		};
	});

	// 2- Page filters that reach the chart
	const { main, perCollection } = getApplicablePageFilters(
		pageFilters,
		collection,
		multiCollections
	);

	// Regular page filters sent to the query (the special date is handled apart)
	const pageMap = {};
	let special = null;
	for (const entry of Object.values(main)) {
		const value = getMappedPageValue(entry.filter);
		if (value === undefined) continue; // not sent (empty date)
		const item = { ...entry, value };
		if (entry.sourceKey === SPECIAL_DATE_KEY && !entry.viaLink) {
			special = item;
		} else {
			pageMap[entry.filter.name ?? entry.filter.attribute ?? entry.mapKey] = item;
		}
	}

	// 3- Widget filters
	const widgetFilters = lod_.pickBy(chart?.filters ?? {}, value => !isEmptyValue(value));

	// 4- Special date: always mapped on the chart date attribute, overriding any other filter on it
	const dateAttribute = request.filterDateAttribute;
	const otherDateAttributes = [
		...(request.sources ?? []).map(source => source?.filterDateAttribute),
		...(chart?.collections ?? []).map(c => c?.filterDateAttribute)
	].filter(attribute => !lod_.isEmpty(attribute));
	// Field the page special date is mapped on (best effort for sources / multi collections charts)
	const specialField = !lod_.isEmpty(dateAttribute) ? dateAttribute : otherDateAttributes[0];
	const specialFieldLabel = lod_.isNil(getFieldItem(dictionary, specialField)?.label)
		? defaultDateLabel
		: getFieldLabel(dictionary, specialField, language);
	const specialApplies =
		special !== null && (!lod_.isEmpty(dateAttribute) || otherDateAttributes.length > 0);

	// Filters on this field (widget or page) are overridden by the special date
	const isOverriddenBySpecial = key =>
		specialApplies && !lod_.isEmpty(dateAttribute) && key === dateAttribute;

	// 5- Rows (the page always wins: an overridden filter is overridden by a page filter)
	const getDefinition = entry => definitions.find(d => d.attribute === entry.sourceKey);

	const widgetRows = Object.keys(widgetFilters).map(key => {
		const item = getFieldItem(dictionary, key);
		const described = key.startsWith("$")
			? { method: "raw", values: [stringify(widgetFilters[key])] }
			: describeFilterValue(widgetFilters[key], item?.codes, language);
		return {
			key,
			label: getFieldLabel(dictionary, key, language),
			...described,
			overridden: isOverriddenBySpecial(key) || !lod_.isNil(pageMap[key])
		};
	});

	const buildPageRow = (entry, extra = {}) => {
		const definition = getDefinition(entry);
		// Dates as displayed by the page date filter, when provided (the value sent to the back may be encoded)
		const displayDate = options.pageDisplayDates?.[entry.sourceKey];
		return {
			key: entry.mapKey,
			label: getPageFilterLabel(definition, entry.sourceKey, language),
			...describeFilterValue(displayDate ?? entry.value, getDefinitionCodes(definition), language),
			isSpecialDate: false,
			viaLink: entry.viaLink,
			overridden: false,
			...extra
		};
	};

	const pageRows = [];
	if (specialApplies) {
		pageRows.push(
			buildPageRow(special, { key: specialField, label: specialFieldLabel, isSpecialDate: true })
		);
	}
	for (const [key, entry] of Object.entries(pageMap)) {
		pageRows.push(buildPageRow(entry, { overridden: isOverriddenBySpecial(key) }));
	}
	for (const entry of perCollection) {
		if (entry.sourceKey === SPECIAL_DATE_KEY) continue;
		const value = getMappedPageValue(entry.filter);
		if (value !== undefined) {
			pageRows.push(buildPageRow({ ...entry, value }));
		}
	}

	const rows = [...widgetRows, ...pageRows];

	return {
		sources,
		widgetRows,
		pageRows,
		count: rows.filter(row => !row.overridden).length,
		hasOverride: rows.some(row => row.overridden)
	};
}

export default computeChartFiltersContext;
